/**
 * The FutureX chatbox: a fixed input bar pinned to the bottom of the terminal
 * with streaming output flowing above it — the experience you'd get from an Ink
 * TUI, hand-rolled with raw ANSI so it needs zero dependencies.
 *
 * Rendering model (the `log-update` pattern): finished output is *committed* to
 * the real terminal scrollback, and only a small DYNAMIC block at the bottom is
 * repainted in place — a live stream preview, an optional spinner (with elapsed
 * time), a full-width separator rule, the input line (with a fake caret; the
 * real cursor is hidden), the `/`-command suggestions, and a status line with a
 * state dot. Before printing any committed output we erase the dynamic block,
 * write the output (it scrolls into history), then redraw the block underneath —
 * so the input bar always sits at the bottom and never gets interleaved with
 * streaming text.
 *
 * The chatbox is an {@link OutputSink}: install it with `setOutputSink(box)` and
 * every `line`/`info`/`out`/`startSpinner` call elsewhere lands here.
 */
import { emitKeypressEvents } from 'node:readline';
import { color, type OutputSink } from './ui.js';
import { MarkdownStream, styleInline } from './markdown.js';
import type { AskFn, GateAnswer } from './permissions.js';

/** ASCII escape (0x1B) — derived from char code to avoid literal-escape issues. */
const ESC = String.fromCharCode(27);
const SPINNER_FRAMES = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'];
const PROMPT = '❯ ';
/** Hard ceiling on a single paste — finalizes even if paste-end never arrives. */
const MAX_PASTE = 2_000_000;

/** Minimal shape of the keypress event node's readline emits in raw mode. */
interface KeyInfo {
  sequence?: string;
  name?: string;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
}

/** A row in the `/`-command suggestion dropdown. */
export interface Suggestion {
  /** The command inserted on Tab (e.g. `/model`). */
  value: string;
  /** A short dim description shown beside it. */
  hint: string;
}

/** The session state reflected in the status dot + prompt glyph. */
export type ChatboxState = 'ready' | 'working';

/** Callbacks the REPL wires into the chatbox. */
export interface ChatboxHandlers {
  /** Enter pressed — the (possibly empty) submitted line. */
  onSubmit(text: string): void;
  /** Ctrl+C — cancel a running turn, or quit when idle. */
  onInterrupt(): void;
  /** Ctrl+D on an empty line (EOF) — quit. */
  onExit(): void;
  /** The input buffer changed (drives live `/` suggestions). */
  onChange?(buffer: string): void;
  /** Shift+Tab — toggle the permission mode (ask ↔ auto). */
  onToggleAuto?(): void;
}

export interface ChatboxOptions {
  /** Dynamic status text rendered (dimmed) on the bottom line. */
  status(): string;
  /** Current session state — drives the status dot colour. Default 'ready'. */
  state?(): ChatboxState;
  handlers: ChatboxHandlers;
}

/** Display width of one code point, biased to over-estimate so we never wrap. */
function cellWidth(cp: number): number {
  if (cp === 0) return 0;
  // Zero-width: combining marks, zero-width spaces, BOM.
  if ((cp >= 0x0300 && cp <= 0x036f) || (cp >= 0x200b && cp <= 0x200f) || cp === 0xfeff) return 0;
  // ❯ (the prompt ornament) is East-Asian-Width Neutral — one cell — even though
  // it sits inside the wide symbol block below. Special-case it so the prompt
  // width, input scroll window, and echoed highlight band all measure correctly.
  if (cp === 0x276f) return 1;
  // Wide: CJK, Hangul, Kana, fullwidth, and the common emoji/symbol blocks.
  if (
    (cp >= 0x1100 && cp <= 0x115f) ||
    (cp >= 0x231a && cp <= 0x23ff) || // misc technical (⌚ ⏳ ⏰ …)
    (cp >= 0x2600 && cp <= 0x27bf) || // misc symbols + dingbats
    (cp >= 0x2b00 && cp <= 0x2bff) ||
    (cp >= 0x2e80 && cp <= 0x303e) ||
    (cp >= 0x3041 && cp <= 0x33ff) ||
    (cp >= 0x3400 && cp <= 0x4dbf) ||
    (cp >= 0x4e00 && cp <= 0x9fff) ||
    (cp >= 0xa000 && cp <= 0xa4cf) ||
    (cp >= 0xac00 && cp <= 0xd7a3) ||
    (cp >= 0xf900 && cp <= 0xfaff) ||
    (cp >= 0xfe30 && cp <= 0xfe4f) ||
    (cp >= 0xff00 && cp <= 0xff60) ||
    (cp >= 0xffe0 && cp <= 0xffe6) ||
    (cp >= 0x1f000 && cp <= 0x1f0ff) ||
    (cp >= 0x1f300 && cp <= 0x1faff)
  ) {
    return 2;
  }
  return 1;
}

function widthOf(plain: string): number {
  let w = 0;
  for (const ch of plain) w += cellWidth(ch.codePointAt(0) ?? 0);
  return w;
}

/** Truncate plain text (no ANSI) to `max` display columns, adding `…` when cut. */
function fitWidth(plain: string, max: number): string {
  if (widthOf(plain) <= max) return plain;
  let w = 0;
  let out = '';
  for (const ch of plain) {
    const cw = cellWidth(ch.codePointAt(0) ?? 0);
    if (w + cw > max - 1) break; // reserve 1 col for the ellipsis
    out += ch;
    w += cw;
  }
  return `${out}…`;
}

/** Keep the TAIL of plain text within `max` columns (for the live stream preview). */
function fitTail(plain: string, max: number): string {
  if (widthOf(plain) <= max) return plain;
  const chars = [...plain];
  let w = 0;
  const picked: string[] = [];
  for (let i = chars.length - 1; i >= 0; i--) {
    const ch = chars[i] ?? '';
    const cw = cellWidth(ch.codePointAt(0) ?? 0);
    if (w + cw > max - 1) break;
    picked.push(ch);
    w += cw;
  }
  picked.reverse();
  return `…${picked.join('')}`;
}

/**
 * Compose the ANSI needed to turn the on-screen block `from` into `to` in a
 * SINGLE atomic write — the anti-flicker core. Splitting the old erase() and
 * draw() into two `stdout.write`s let the terminal present the erased (blank)
 * frame before the redraw; that momentary blank IS the "input box keeps
 * disappearing" flicker. Here every line is overwritten in place (`ESC[0K`
 * clears just to end-of-line, not the whole screen), the block is re-anchored by
 * moving the cursor up over the previous frame, and a shrink is handled by one
 * `ESC[0J` below the new block. The caller writes the returned string in one go.
 *
 * Invariant: the cursor sits on the LAST line of `from` when called, and is left
 * on the last line of `to`. Lines never wrap (callers width-cap to cols-1), so a
 * line is exactly one physical row. `\r` before each line's content makes column
 * placement independent of the terminal's LF→CRLF (ONLCR) setting.
 */
export function composeFrame(from: readonly string[], to: readonly string[]): string {
  let out = '\r';
  if (from.length > 1) out += `${ESC}[${from.length - 1}A`; // up to the block's first row
  for (let i = 0; i < to.length; i++) {
    if (i > 0) out += '\n'; // next row (creates a new one at the bottom when the block grows)
    out += `\r${to[i] ?? ''}${ESC}[0K`; // col 0, content, clear the rest of this row
  }
  if (to.length < from.length) {
    // The block shrank — old rows still sit below. Drop below the new block, wipe
    // everything to end-of-screen, then climb back onto the new last row.
    out += `\n\r${ESC}[0J${ESC}[1A`;
  }
  return out;
}

/**
 * Normalize pasted text for insertion into the input buffer: unify CRLF / lone
 * CR to `\n`, keep tabs and newlines, and drop other control characters (a
 * bracketed paste can still carry stray escapes on some terminals). The result
 * is inserted verbatim and later submitted as ONE prompt, so a multi-line paste
 * never fragments into multiple submissions.
 */
export function normalizePastedText(raw: string): string {
  const unified = raw.replace(/\r\n?/g, '\n');
  let out = '';
  for (const ch of unified) {
    const c = ch.codePointAt(0) ?? 0;
    if (c === 0x0a || c === 0x09) out += ch; // keep newline + tab
    else if (c >= 0x20 && c !== 0x7f) out += ch; // keep printable
  }
  return out;
}

/**
 * The fixed-input chatbox. Owns stdin (raw mode) and the bottom of stdout while
 * mounted. Construct, {@link start}, install as the output sink, and the REPL
 * drives it via handlers + the OutputSink methods.
 */
export class Chatbox implements OutputSink {
  private buffer = '';
  private caret = 0; // code-unit index into buffer
  private readonly history: string[] = [];
  private histIndex = -1; // -1 = editing live buffer; else browsing history
  private histStash = '';
  private liveBuf = ''; // current uncommitted streaming line (no newline)
  private readonly md = new MarkdownStream(); // renders streamed answer lines as ANSI markdown
  private spinnerLabel: string | null = null;
  private spinnerFrame = 0;
  private spinnerSince = 0; // epoch ms when the current spinner appeared
  private suggestions: Suggestion[] = [];
  private sugIndex = -1; // highlighted suggestion row (-1 = none; arrows move it)
  private pasting = false; // inside a bracketed paste (ESC[200~ … ESC[201~)
  private pasteBuf = ''; // raw bytes accumulated during the current paste
  private frameLines: string[] = []; // the dynamic block currently on screen (for atomic diff)
  private cols = 80;
  private started = false;
  private gatePrompt: string | null = null;
  private gateResolve: ((ans: 'y' | 'n' | 'a' | 'd') => void) | null = null;
  private gateChain: Promise<unknown> = Promise.resolve();
  private spinTimer: ReturnType<typeof setInterval> | null = null;
  private readonly statusFn: () => string;
  private readonly stateFn: () => ChatboxState;
  private readonly handlers: ChatboxHandlers;
  private readonly onKey = (str: string | undefined, key: KeyInfo | undefined): void =>
    this.handleKey(str, key);
  private readonly onResize = (): void => {
    this.cols = process.stdout.columns ?? 80;
    // On resize the terminal reflows already-printed rows, so our frameLines no
    // longer map 1:1 to physical rows. Diffing against them would move the cursor
    // up by the wrong count and corrupt the redraw — repaint fresh instead (the
    // previous, reflowed bar is left as harmless scrollback and scrolls away).
    this.frameLines = [];
    this.render();
  };
  // Best-effort terminal restore if the process exits without stop() (uncaught
  // exception, process.exit, an unhandled rejection). start() writes ?2004h + ?25l
  // + raw mode, which Node never undoes on its own — leaving the user's shell with
  // bracketed paste on and the cursor hidden. Runs once; stop() removes it.
  private readonly onProcExit = (): void => {
    try {
      process.stdout.write(`${ESC}[?2004l${ESC}[?25h`);
      if (process.stdin.isTTY) process.stdin.setRawMode(false);
    } catch {
      /* nothing else we can do from an exit handler */
    }
  };

  constructor(opts: ChatboxOptions) {
    this.statusFn = opts.status;
    this.stateFn = opts.state ?? ((): ChatboxState => 'ready');
    this.handlers = opts.handlers;
  }

  /** Enter raw mode, hide the cursor, and paint the input bar. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.cols = process.stdout.columns ?? 80;
    this.frameLines = [];
    emitKeypressEvents(process.stdin);
    if (process.stdin.isTTY) process.stdin.setRawMode(true);
    process.stdin.resume();
    process.stdin.on('keypress', this.onKey);
    process.stdout.on('resize', this.onResize);
    process.once('exit', this.onProcExit); // restore the terminal even on a crash
    // Hide the real cursor (we draw a fake one) and turn ON bracketed paste, so a
    // multi-line paste arrives wrapped in ESC[200~ … ESC[201~ (see handleKey).
    process.stdout.write(`${ESC}[?25l${ESC}[?2004h`);
    this.spinTimer = setInterval(() => {
      if (this.spinnerLabel !== null && this.liveBuf === '') {
        this.spinnerFrame = (this.spinnerFrame + 1) % SPINNER_FRAMES.length;
        this.render();
      }
    }, 90);
    this.spinTimer.unref?.();
    this.paint();
  }

  /** Tear down: clear the bar, restore the cursor and cooked mode. */
  stop(): void {
    if (!this.started) return;
    this.erase();
    this.started = false;
    if (this.spinTimer) {
      clearInterval(this.spinTimer);
      this.spinTimer = null;
    }
    process.stdin.removeListener('keypress', this.onKey);
    process.stdout.removeListener('resize', this.onResize);
    process.removeListener('exit', this.onProcExit);
    process.stdout.write(`${ESC}[?2004l${ESC}[?25h`); // disable bracketed paste + restore cursor
    if (process.stdin.isTTY) {
      try {
        process.stdin.setRawMode(false);
      } catch {
        /* not a TTY anymore — ignore */
      }
    }
    process.stdin.pause();
  }

  // --- OutputSink -----------------------------------------------------------

  line(text: string): void {
    // Structured output (tool calls, info, phase rules) ends the current answer
    // segment — clear a dangling code fence so the next answer's prose isn't
    // wrongly rendered as code for the rest of the turn.
    if (this.md.inCodeBlock) this.md.reset();
    this.commit(text);
  }

  partial(text: string): void {
    this.liveBuf += text.replace(/\r\n?/g, '\n'); // normalize CRLF/CR so line-splitting + markdown work
    let nl = this.liveBuf.indexOf('\n');
    while (nl !== -1) {
      const done = this.liveBuf.slice(0, nl);
      this.liveBuf = this.liveBuf.slice(nl + 1);
      // A completed streamed line is rendered as styled Markdown before it scrolls
      // into history — the answer looks alive instead of raw `**asterisks**`.
      this.commit(this.md.line(done));
      nl = this.liveBuf.indexOf('\n');
    }
    this.render();
  }

  spinner(label: string | null): void {
    if (label !== null && this.spinnerLabel === null) this.spinnerSince = Date.now();
    this.spinnerLabel = label;
    this.render();
  }

  // --- driven by the REPL ---------------------------------------------------

  /** Commit any half-streamed line (call when a turn ends). */
  flushLive(): void {
    if (this.liveBuf) {
      const rest = this.liveBuf;
      this.liveBuf = '';
      this.commit(this.md.line(rest));
    }
    // Turn boundary: reset so an unclosed ``` fence can't bleed into the next answer.
    this.md.reset();
  }

  setSuggestions(list: Suggestion[]): void {
    this.suggestions = list;
    // Reset the highlight whenever the list changes (i.e. the user typed): Enter
    // then submits by default; arrow keys re-engage selection.
    this.sugIndex = -1;
    // No render here: this is only ever called from the REPL's onChange, which
    // the chatbox invokes immediately before its own render() (afterEdit /
    // historyPrev / historyNext). Rendering here too would paint every keystroke
    // twice.
  }

  /** Force a repaint (e.g. the status line changed). */
  refresh(): void {
    this.render();
  }

  /**
   * Modal permission prompt: show it in place of the input line and resolve with
   * the user's single-key answer. The in-progress input buffer is preserved.
   * Concurrent calls are serialized — a second prompt waits for the first.
   */
  askGate(prompt: string): Promise<'y' | 'n' | 'a' | 'd'> {
    const show = (): Promise<'y' | 'n' | 'a' | 'd'> =>
      new Promise((resolve) => {
        this.gatePrompt = prompt;
        this.gateResolve = (ans) => {
          this.gatePrompt = null;
          this.gateResolve = null;
          this.render();
          resolve(ans);
        };
        this.render();
      });
    const next = this.gateChain.then(show, show);
    this.gateChain = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }

  // --- rendering ------------------------------------------------------------

  /** Repaint the dynamic block in ONE atomic write (see {@link composeFrame}). */
  private paint(): void {
    if (!this.started) return;
    const next = this.buildBlock();
    process.stdout.write(composeFrame(this.frameLines, next));
    this.frameLines = next;
  }

  /**
   * Commit finished rows to scrollback ABOVE the bar, then redraw the bar — all
   * in one write so the committed text and the redraw never flash apart.
   */
  private commitLines(rows: string[]): void {
    if (!this.started) {
      process.stdout.write(`${rows.join('\n')}\n`);
      return;
    }
    const next = this.buildBlock();
    let out = '\r';
    if (this.frameLines.length > 1) out += `${ESC}[${this.frameLines.length - 1}A`;
    out += `${ESC}[0J`; // clear the old block region
    out += `${rows.join('\n')}\n`; // committed line(s) scroll into history
    out += composeFrame([], next); // redraw the bar underneath
    process.stdout.write(out);
    this.frameLines = next;
  }

  private commit(text: string): void {
    this.commitLines([text]);
  }

  private erase(): void {
    if (this.frameLines.length === 0) return;
    let s = '\r';
    if (this.frameLines.length > 1) s += `${ESC}[${this.frameLines.length - 1}A`;
    s += `${ESC}[0J`; // clear from cursor to end of screen
    process.stdout.write(s);
    this.frameLines = [];
  }

  private render(): void {
    this.paint();
  }

  private buildBlock(): string[] {
    const w = Math.max(8, this.cols - 1); // leave a column of slack to avoid wrap
    const lines: string[] = [];
    const working = this.stateFn() === 'working';

    // 1. live streaming preview, or the spinner while we wait for the first token.
    if (this.liveBuf) {
      const tail = fitTail(this.liveBuf.replace(/\s+/g, ' '), w - 2);
      // Inline-style the in-progress line so bold/code/etc. render as they complete;
      // inside a fenced code block leave it raw (dim). A live cyan bar marks it.
      const shown = this.md.inCodeBlock ? color.dim(tail) : styleInline(tail);
      lines.push(`${color.cyan('┃')} ${shown}`);
    } else if (this.spinnerLabel !== null) {
      const frame = SPINNER_FRAMES[this.spinnerFrame % SPINNER_FRAMES.length] ?? '';
      const secs = Math.floor((Date.now() - this.spinnerSince) / 1000);
      const elapsed = secs >= 2 ? ` · ${secs}s` : '';
      lines.push(
        `${color.cyan(frame)} ${color.dim(fitWidth(`${this.spinnerLabel}${elapsed}`, w - 2))}`,
      );
    }

    // 2. full-width separator rule.
    lines.push(color.dim('─'.repeat(w)));

    // 3. input line, or the modal permission prompt.
    if (this.gatePrompt) {
      lines.push(`${color.yellow('⚠ permission')} ${color.bold(fitWidth(this.gatePrompt, w - 14))}`);
      const plainKeys = '  [y] yes   [n] no   [a] always allow   [d] always deny   (enter = no)';
      if (widthOf(plainKeys) <= w) {
        lines.push(
          `  ${color.green('[y]')} yes   ${color.red('[n]')} no   ${color.cyan('[a]')} always allow   ` +
            `${color.cyan('[d]')} always deny   ${color.dim('(enter = no)')}`,
        );
      } else {
        lines.push(color.dim(fitWidth(plainKeys, w)));
      }
    } else {
      lines.push(...this.renderInputLines(w, working));
      for (const [i, sug] of this.suggestions.slice(0, 6).entries()) {
        const text = fitWidth(`  ${sug.value.padEnd(14)} ${sug.hint}`, w);
        // Only the actively arrow-navigated row is highlighted (reverse-video), so
        // the visual matches what Enter does: highlighted → select, else → submit.
        // (Tab still completes the first row even with nothing highlighted.)
        lines.push(i === this.sugIndex ? `${ESC}[7m${text}${ESC}[0m` : color.dim(text));
      }
    }

    // 4. status line with a state dot.
    const dot = working ? color.yellow('●') : color.green('●');
    lines.push(`${dot} ${color.dim(fitWidth(this.statusFn(), w - 2))}`);

    return lines;
  }

  /**
   * The input area — one row while editing normally, or several when the buffer
   * holds a multi-line paste. Only the caret's row scrolls horizontally and shows
   * the fake caret; a very long paste is windowed around the caret with "+N
   * above/below" markers so the block height stays bounded.
   */
  private renderInputLines(w: number, working: boolean): string[] {
    const glyph = working ? color.yellow(PROMPT) : color.magenta(PROMPT);
    const promptW = widthOf(PROMPT);
    const avail = Math.max(4, w - promptW);
    const logical = this.buffer.split('\n');

    if (logical.length === 1) {
      return [glyph + this.renderCaretLine((logical[0] ?? '').replace(/\t/g, ' '), this.caret, avail)];
    }

    // Locate the caret's (row, col) among the logical lines.
    let acc = 0;
    let caretRow = logical.length - 1;
    let caretCol = (logical[logical.length - 1] ?? '').length;
    for (let r = 0; r < logical.length; r++) {
      const len = (logical[r] ?? '').length;
      if (this.caret <= acc + len) {
        caretRow = r;
        caretCol = this.caret - acc;
        break;
      }
      acc += len + 1; // + the newline
    }

    // Window the rows so a huge paste never blows up the block height — and cap by
    // the viewport height so the whole block can't exceed a short terminal (which
    // would break the cursor-up anchor in composeFrame).
    const MAX = Math.max(3, Math.min(8, (process.stdout.rows ?? 24) - 5));
    let start = 0;
    let end = logical.length;
    if (logical.length > MAX) {
      start = Math.max(0, Math.min(caretRow - Math.floor(MAX / 2), logical.length - MAX));
      end = start + MAX;
    }

    const out: string[] = [];
    if (start > 0) out.push(color.dim(`  ⋯ +${start} line${start === 1 ? '' : 's'} above`));
    const cont = ' '.repeat(promptW); // align continuation rows under the first
    for (let r = start; r < end; r++) {
      const disp = (logical[r] ?? '').replace(/\t/g, ' ');
      const prefix = r === 0 ? glyph : color.dim(cont);
      const body =
        r === caretRow ? this.renderCaretLine(disp, caretCol, avail) : fitWidth(disp, avail);
      out.push(prefix + body);
    }
    if (end < logical.length) {
      const below = logical.length - end;
      out.push(color.dim(`  ⋯ +${below} line${below === 1 ? '' : 's'} below`));
    }
    return out;
  }

  /** One text row with a reverse-video fake caret, h-scrolled to keep it visible. */
  private renderCaretLine(text: string, caret: number, avail: number): string {
    const winChars = Math.max(3, avail - 1); // reserve one cell for the caret block
    const start = caret > winChars ? caret - winChars : 0;
    const visible = text.slice(start, start + winChars);
    const rel = caret - start;
    const before = visible.slice(0, rel);
    const atChar = visible.slice(rel, rel + 1) || ' ';
    const after = visible.slice(rel + 1);
    return `${before}${ESC}[7m${atChar}${ESC}[0m${after}`;
  }

  /** A submitted prompt echoed to scrollback as full-width highlighted band(s). */
  private echoPrompt(text: string): string[] {
    const w = Math.max(8, this.cols - 1);
    const logical = text.split('\n');
    const MAX = 20;
    const rows = logical.slice(0, MAX).map((ln, i) => {
      const body = fitWidth(`${i === 0 ? '❯ ' : '  '}${ln.replace(/\t/g, ' ')}`, w);
      const pad = ' '.repeat(Math.max(0, w - widthOf(body)));
      return color.highlight(body + pad);
    });
    if (logical.length > MAX) {
      rows.push(color.dim(`  ⋯ +${logical.length - MAX} more lines`));
    }
    return rows;
  }

  // --- input ----------------------------------------------------------------

  private handleKey(str: string | undefined, key: KeyInfo | undefined): void {
    if (!this.started) return;
    const k = key ?? {};

    // Bracketed paste is handled FIRST — even ahead of the permission-gate modal.
    // Otherwise pasted characters are fed to the gate one-by-one and the first
    // y/n/a/d in the pasted text silently answers it (a paste starting with 'a'
    // would PERSIST always-allow). Swallow everything between paste-start and
    // paste-end and insert it as ONE edit, so a multi-line paste is one prompt.
    if (k.name === 'paste-start') {
      this.pasting = true;
      this.pasteBuf = '';
      return;
    }
    if (this.pasting) {
      // Escape hatch: a real Ctrl+C aborts the paste, so a dropped paste-end
      // (flaky ssh / tmux) can never wedge the prompt.
      if (k.ctrl && k.name === 'c') {
        this.pasting = false;
        this.pasteBuf = '';
        return this.handlers.onInterrupt();
      }
      if (k.name === 'paste-end' || this.pasteBuf.length >= MAX_PASTE) {
        this.pasting = false;
        const clean = normalizePastedText(this.pasteBuf);
        this.pasteBuf = '';
        // A paste that lands while a permission modal is open is ignored — never
        // treated as the gate answer, never injected into the hidden buffer.
        if (this.gateResolve) {
          this.render();
          return;
        }
        if (clean) {
          this.buffer = this.buffer.slice(0, this.caret) + clean + this.buffer.slice(this.caret);
          this.caret += clean.length;
          this.afterEdit();
        } else {
          this.render();
        }
        return;
      }
      this.pasteBuf += str ?? '';
      return;
    }

    // Modal gate: capture only plain y / n / a / d (Enter and Ctrl+C default to
    // "no"); other modified keys (Ctrl+A, Ctrl+D, …) must not answer the prompt.
    if (this.gateResolve) {
      const resolve = this.gateResolve;
      if (k.ctrl && k.name === 'c') return resolve('n');
      if (k.ctrl || k.meta) return;
      if (k.name === 'return' || k.name === 'enter') return resolve('n');
      const ch = (str ?? k.name ?? '').toLowerCase();
      if (ch === 'y' || ch === 'n' || ch === 'a' || ch === 'd') return resolve(ch);
      return;
    }

    if (k.ctrl && k.name === 'c') return this.handlers.onInterrupt();
    if (k.ctrl && k.name === 'd') {
      if (this.buffer === '') return this.handlers.onExit();
      this.buffer = this.buffer.slice(0, this.caret) + this.buffer.slice(this.caret + 1);
      return this.afterEdit();
    }

    // Shift+Tab toggles the permission mode (ask ↔ auto). It's a non-printable
    // chord (readline reports name 'tab' + shift), so unlike a letter it never
    // collides with typing and is safe to fire anytime. Plain Tab (shift:false)
    // falls through to the completion case below.
    if (k.name === 'tab' && k.shift === true) {
      this.handlers.onToggleAuto?.();
      return;
    }

    switch (k.name) {
      case 'return':
      case 'enter': {
        // With a suggestion actively highlighted (via the arrows), Enter SELECTS
        // it instead of submitting. Otherwise Enter submits the whole buffer —
        // including any newlines from a paste, so it goes out as one prompt.
        if (this.suggestions.length > 0 && this.sugIndex >= 0) {
          this.applySuggestion(this.sugIndex);
          return;
        }
        const text = this.buffer;
        this.buffer = '';
        this.caret = 0;
        this.histIndex = -1;
        const trimmed = text.trim();
        if (trimmed) {
          this.history.push(text);
          if (this.history.length > 200) this.history.shift();
        }
        this.suggestions = [];
        this.sugIndex = -1;
        // Echo the submitted prompt to scrollback as a highlighted band so past
        // prompts stay visible above the streamed answer.
        if (trimmed) this.commitLines(this.echoPrompt(text));
        this.handlers.onSubmit(text);
        this.render();
        return;
      }
      case 'backspace':
        if (this.caret > 0) {
          this.buffer = this.buffer.slice(0, this.caret - 1) + this.buffer.slice(this.caret);
          this.caret -= 1;
        }
        return this.afterEdit();
      case 'delete':
        this.buffer = this.buffer.slice(0, this.caret) + this.buffer.slice(this.caret + 1);
        return this.afterEdit();
      case 'left':
        if (this.caret > 0) this.caret -= 1;
        return this.render();
      case 'right':
        if (this.caret < this.buffer.length) this.caret += 1;
        return this.render();
      case 'home':
        this.caret = 0;
        return this.render();
      case 'end':
        this.caret = this.buffer.length;
        return this.render();
      case 'up':
        // Arrows drive the dropdown while TYPING; but once you're browsing history
        // (histIndex !== -1), keep browsing even if a recalled '/cmd' auto-opened
        // the dropdown — otherwise a single-token slash command traps navigation.
        if (this.suggestions.length > 0 && this.histIndex === -1) {
          this.sugIndex = Math.max(-1, this.sugIndex - 1);
          return this.render();
        }
        return this.historyPrev();
      case 'down':
        if (this.suggestions.length > 0 && this.histIndex === -1) {
          this.sugIndex = Math.min(this.suggestions.length - 1, this.sugIndex + 1);
          return this.render();
        }
        return this.historyNext();
      case 'tab':
        // Tab completes the highlighted suggestion (or the first when none is).
        if (this.suggestions.length > 0) {
          this.applySuggestion(this.sugIndex >= 0 ? this.sugIndex : 0);
        }
        return;
      default:
        break;
    }

    if (k.ctrl) {
      switch (k.name) {
        case 'a':
          this.caret = 0;
          return this.render();
        case 'e':
          this.caret = this.buffer.length;
          return this.render();
        case 'u':
          this.buffer = this.buffer.slice(this.caret);
          this.caret = 0;
          return this.afterEdit();
        case 'k':
          this.buffer = this.buffer.slice(0, this.caret);
          return this.afterEdit();
        case 'w':
          return this.deleteWord();
        default:
          return;
      }
    }

    // Printable insertion (strip any stray control chars).
    if (str && !k.ctrl && !k.meta) {
      const clean = [...str]
        .filter((ch) => {
          const c = ch.codePointAt(0) ?? 0;
          return c >= 0x20 && c !== 0x7f;
        })
        .join('');
      if (clean) {
        this.buffer = this.buffer.slice(0, this.caret) + clean + this.buffer.slice(this.caret);
        this.caret += clean.length;
        this.afterEdit();
      }
    }
  }

  /** Complete a suggestion into the buffer (Tab, or Enter on a highlighted row). */
  private applySuggestion(i: number): void {
    const sug = this.suggestions[i];
    if (!sug) return;
    this.buffer = `${sug.value} `;
    this.caret = this.buffer.length;
    this.suggestions = [];
    this.sugIndex = -1;
    this.afterEdit();
  }

  private afterEdit(): void {
    this.histIndex = -1;
    this.handlers.onChange?.(this.buffer);
    this.render();
  }

  private historyPrev(): void {
    if (this.history.length === 0) return;
    if (this.histIndex === -1) {
      this.histStash = this.buffer;
      this.histIndex = this.history.length - 1;
    } else if (this.histIndex > 0) {
      this.histIndex -= 1;
    }
    this.buffer = this.history[this.histIndex] ?? '';
    this.caret = this.buffer.length;
    this.handlers.onChange?.(this.buffer);
    this.render();
  }

  private historyNext(): void {
    if (this.histIndex === -1) return;
    if (this.histIndex < this.history.length - 1) {
      this.histIndex += 1;
      this.buffer = this.history[this.histIndex] ?? '';
    } else {
      this.histIndex = -1;
      this.buffer = this.histStash;
    }
    this.caret = this.buffer.length;
    this.handlers.onChange?.(this.buffer);
    this.render();
  }

  private deleteWord(): void {
    let i = this.caret;
    while (i > 0 && this.buffer[i - 1] === ' ') i -= 1;
    while (i > 0 && this.buffer[i - 1] !== ' ') i -= 1;
    this.buffer = this.buffer.slice(0, i) + this.buffer.slice(this.caret);
    this.caret = i;
    this.afterEdit();
  }
}

/**
 * A permission prompt backed by the chatbox's modal: pauses for a single
 * keypress. `[a]lways allow` / `[d] always deny` are persisted per action kind
 * by the permission gate that wraps this ask (see permissions.ts).
 */
export function createChatboxAsk(box: Chatbox): AskFn {
  const toAnswer: Record<'y' | 'n' | 'a' | 'd', GateAnswer> = {
    y: 'yes',
    n: 'no',
    a: 'always-allow',
    d: 'always-deny',
  };
  return async (req) => {
    const caution = req.kind === 'run' ? ' — runs with your shell access' : '';
    const ans = await box.askGate(`${req.kind} · ${req.preview}${caution}`);
    return toAnswer[ans];
  };
}
